/* src/components/GroupDetails.js - v2.19 - Local Reaction Updates
   CHANGELOG (v2.18 -> v2.19): Same fix as Profile.js
   v6.32: v2.18's own paginated fetch meant liking/commenting/
   voting/posting from this page updated App.js's global state but not
   groupPosts (this page's own separate copy) - the action worked, the
   UI just didn't visibly reflect it. App.js's handlers now return the
   mutation's result (App.js v13.41), so this wraps each one (including
   onAddPost, which prepends to groupPosts here since new group posts
   don't otherwise appear in App.js's main feed at all) to patch
   groupPosts locally too.
   CHANGELOG (v2.17 -> v2.18): groupPosts used to filter
   the full `posts` array (a prop from App.js) down to this group's slug
   client-side - only worked because App.js was downloading every post in
   the database. Now that the main feed is paginated (App.js/routes/
   posts.py v2.13), this fetches its own paginated page via the extended
   GET /api/posts/archive?group=<slug>, with infinite-scroll load-more
   wired into its <Feed> the same way App.js's main feed does. Known
   limitation, same as Profile.js v6.31: a like/comment/vote/new-post made
   from this page doesn't update in place here (still succeeds server-
   side) since groupPosts is now a separate fetch from App.js's own
   `posts` state - re-opening the group picks up the change.
   CHANGELOG (v2.16 -> v2.17): Per explicit request, groups keep the
   existing open self-join (no public/private visibility toggle was
   added - deliberately dropped, private-by-default is the point: it
   gives users an incentive to join). Added invites as a growth path
   layered on top of that, backed by routes/groups.py v3.0:
   - Settings > General now has a "Who can invite people?" select
     (admins_only / all_members), persisted the same way every other
     setting already is (handleSaveSettings spreads editFields into the
     existing PUT).
   - New "+ Invite" button next to the member badge, shown when
     isMember && (canManage || group.invite_permission === 'all_members')
     (new canInvite). Opens a friends-only picker (same fetch/filter
     pattern as Messages.js's New Chat modal) excluding anyone already a
     member, and POSTs to the new /invite endpoint per selection.
   - fetchGroup now passes ?current_user= so the backend can include
     invited_by when the viewer has a pending invite to this exact group
     - when present, both the header's Join button and the feed tab's
     "Members Only" gate are replaced with an Accept/Decline pair instead
     (handleRespondInvite), rather than the generic Join Now action.
   CHANGELOG (v2.15 -> v2.16): Accepts and forwards the new onVoteComment
   prop (App.js v13.36) into its own internal <Feed>, same as
   onAddComment already is - group post comments now support up/down
   voting too, not just the main feed/profile.
   CHANGELOG (v2.14 -> v2.15):
   - handleLeaveGroup now surfaces the backend's actual error message
     (routes/groups.py v2 now enforces server-side that a group must
     always keep at least one admin - previously this was ONLY blocked by
     hiding the Leave button for the creator via the `!isCreator` guard
     below, which was purely cosmetic: nothing stopped a direct API call
     from removing the sole admin with no restriction at all).
   - The Leave button's guard is now just `isMember` (dropped `!isCreator`)
     - the creator is no longer permanently barred from leaving. Instead
       they're subject to the exact same rule as any other admin: if
       they're the only admin left, they must promote someone else first
       (via the Settings modal's role dropdown, which now actually
       persists - see groups.py v2's members-field PUT fix), or if they're
       the last person in the group at all, leaving disbands it entirely.
   - Added a distinct confirmation message for that disband case, computed
     client-side from the group's current member list before the request
     is even sent, since that's an irreversible action worth a clearer
     warning than the generic "are you sure" prompt.
   CHANGELOG (v2.13 -> v2.14):
   - Fetches the full groups list once (fetchUserGroups) and filters it to
     groups the current user actually belongs to, passed down to Feed as
     userGroups - needed for the "Post to:" checklist added in Feed.js
     v7.5.
   - groupPosts now filters on p.group_slugs?.includes(groupSlug) instead
     of p.group_slug === groupSlug - posts.py v2.3 changed the field from
     a singular slug to a list (group_slugs), supporting one post shared
     to multiple groups.
   - The onAddPost wrapper that manually injected group_slug is removed -
     Feed.js now sets visibility:'group' and the correct group_slugs
     itself when groupContext is passed, so onAddPost is passed straight
     through unmodified. */
import React, { useState, useEffect, useCallback } from 'react';
import Feed from './Feed';

function GroupDetails({
    groupSlug,
    user,
    onAddPost,
    onDeletePost, 
    onLikePost, 
    onAddComment,
    onVoteComment,
    onDeleteComment,
    onEditComment, 
    onEditPost, 
    onNavigateProfile,
    onBack,
    onTriggerLogin
}) {
  
  const [group, setGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('feed');
  const [settingsTab, setSettingsTab] = useState('general');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCandidates, setInviteCandidates] = useState([]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [invitedUsernames, setInvitedUsernames] = useState([]);

  const fetchGroup = useCallback(async () => {
      try {
          // current_user lets the backend include invited_by if the
          // viewer has a pending invite to this specific group (groups.py
          // v3.0) - drives the "Accept invite from @X" UI below in place
          // of the generic Join button.
          const url = user ? `/api/groups/${groupSlug}?current_user=${user.name}` : `/api/groups/${groupSlug}`;
          const res = await fetch(url);
          if (res.ok) {
              const data = await res.json();
              setGroup(data);
          }
      } catch (e) { console.error("Group load error", e); }
  }, [groupSlug, user]);

  const fetchUserGroups = useCallback(async () => {
      if (!user) { setUserGroups([]); return; }
      try {
          const res = await fetch('/api/groups');
          if (res.ok) {
              const data = await res.json();
              const mine = (data || [])
                  .filter(g => (g.members || []).some(m => m.username === user.name))
                  .map(g => ({ slug: g.slug, name: g.name }));
              setUserGroups(mine);
          }
      } catch (e) { console.error("User groups load error", e); }
  }, [user]);

  useEffect(() => {
      if (groupSlug) fetchGroup();
  }, [groupSlug, fetchGroup]);

  useEffect(() => {
      fetchUserGroups();
  }, [fetchUserGroups]);

  const [editFields, setEditFields] = useState({
      name: '', description: '', banner_url: '', icon_url: '', rules: '', members: [], invite_permission: 'admins_only'
  });

  useEffect(() => {
      if (group) {
          setEditFields({
              name: group.name || '',
              description: group.description || '',
              banner_url: group.banner_url || '',
              icon_url: group.icon_url || '',
              rules: group.rules || "1: No Promotions or Spam:\nSelf-promotion (includes other groups), spam, and irrelevant links aren't allowed. Keep it related to the topic of the group. Message an Admin with any questions.\n\n2: Be Kind and Courteous:\nNo hating on others. We're all in this together to create a welcoming environment. Let's treat everyone with respect.\n\n3: Zero Tolerance:\nInstant ban. Enough said. Report any malicious activity to an Admin.\n\n4: Failure to Follow Rules:\nFailure to follow these simple rules (no excuse for not reading the rules) will result in a ban.",
              members: group.members || [],
              invite_permission: group.invite_permission || 'admins_only'
          });
      }
  }, [group]);

  const [groupPosts, setGroupPosts] = useState([]);
  const [groupPostsHasMore, setGroupPostsHasMore] = useState(false);
  const [groupPostsLoading, setGroupPostsLoading] = useState(false);
  const GROUP_POSTS_PAGE_SIZE = 30;

  useEffect(() => {
      if (!groupSlug) { setGroupPosts([]); setGroupPostsHasMore(false); return; }
      setGroupPostsLoading(true);
      const params = new URLSearchParams({ group: groupSlug, limit: GROUP_POSTS_PAGE_SIZE });
      if (user) params.set('current_user', user.name);
      fetch(`/api/posts/archive?${params}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => {
              setGroupPosts(data ? data.posts : []);
              setGroupPostsHasMore(data ? !!data.has_more : false);
          })
          .catch(err => console.error('Group posts fetch error', err))
          .finally(() => setGroupPostsLoading(false));
  }, [groupSlug, user]);

  const loadMoreGroupPosts = useCallback(async () => {
      if (groupPostsLoading || !groupPostsHasMore || groupPosts.length === 0 || !groupSlug) return;
      setGroupPostsLoading(true);
      try {
          const oldest = groupPosts[groupPosts.length - 1]?.id;
          const params = new URLSearchParams({ group: groupSlug, limit: GROUP_POSTS_PAGE_SIZE, before: oldest });
          if (user) params.set('current_user', user.name);
          const res = await fetch(`/api/posts/archive?${params}`);
          if (res.ok) {
              const data = await res.json();
              setGroupPosts(prev => [...prev, ...(data.posts || [])]);
              setGroupPostsHasMore(!!data.has_more);
          }
      } catch (e) { console.error(e); }
      setGroupPostsLoading(false);
  }, [groupPosts, groupPostsHasMore, groupPostsLoading, groupSlug, user]);

  const groupGallery = groupPosts.filter(p => p.image);

  const applyLocalPostUpdate = useCallback((updatedPost) => {
      if (!updatedPost || !updatedPost.id) return;
      setGroupPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  }, []);

  const handleLocalAddPost = useCallback(async (newPost) => {
      const created = await onAddPost(newPost);
      if (created && created.id) setGroupPosts(prev => [created, ...prev]);
  }, [onAddPost]);
  const handleLocalDeletePost = useCallback(async (id) => {
      const ok = await onDeletePost(id);
      if (ok) setGroupPosts(prev => prev.filter(p => p.id !== id));
  }, [onDeletePost]);
  const handleLocalLikePost = useCallback(async (postId, voteType) => {
      applyLocalPostUpdate(await onLikePost(postId, voteType));
  }, [onLikePost, applyLocalPostUpdate]);
  const handleLocalAddComment = useCallback(async (postId, text, image) => {
      applyLocalPostUpdate(await onAddComment(postId, text, image));
  }, [onAddComment, applyLocalPostUpdate]);
  const handleLocalVoteComment = useCallback(async (postId, commentId, vote) => {
      applyLocalPostUpdate(await onVoteComment(postId, commentId, vote));
  }, [onVoteComment, applyLocalPostUpdate]);
  const handleLocalDeleteComment = useCallback(async (postId, commentId) => {
      applyLocalPostUpdate(await onDeleteComment(postId, commentId));
  }, [onDeleteComment, applyLocalPostUpdate]);
  const handleLocalEditComment = useCallback(async (postId, commentId, text) => {
      applyLocalPostUpdate(await onEditComment(postId, commentId, text));
  }, [onEditComment, applyLocalPostUpdate]);
  const handleLocalEditPost = useCallback(async (postId, newText, newVisibility) => {
      applyLocalPostUpdate(await onEditPost(postId, newText, newVisibility));
  }, [onEditPost, applyLocalPostUpdate]);
  
  // GUEST GUARD & MEMBERSHIP CHECK
  const isCreator = user && group?.created_by === user.name;
  const currentUserMember = user && group?.members?.find(m => m.username === user.name);
  let userRole = currentUserMember ? currentUserMember.role : 'visitor';
  if (isCreator) userRole = 'admin';
  const canManage = userRole === 'admin';
  const isMember = !!currentUserMember || isCreator; // Boolean for membership

  const handleJoinGroup = async () => {
      if (!user) { if(onTriggerLogin) onTriggerLogin(); return; }
      try {
          await fetch(`/api/groups/${group.slug}/join`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name })
          });
          fetchGroup(); 
          fetchUserGroups();
      } catch (e) { console.error("Join error", e); }
  };

  const canInvite = isMember && (canManage || group?.invite_permission === 'all_members');

  // --- INVITE FUNCTIONS ---
  const handleOpenInvite = async () => {
      setInviteSearch(''); setInvitedUsernames([]); setShowInviteModal(true);
      try {
          const res = await fetch(`/api/users/directory?view_all=true&current_user=${user.name}`);
          const data = await res.json();
          const memberNames = new Set((group.members || []).map(m => m.username));
          const friendsOnly = (data || []).filter(u => user.friends && user.friends.includes(u.name) && !memberNames.has(u.name));
          setInviteCandidates(friendsOnly);
      } catch (e) { console.error("Invite candidates load error", e); }
  };

  const handleSendInvite = async (targetUsername) => {
      try {
          const res = await fetch(`/api/groups/${group.slug}/invite`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ inviter: user.name, invitee: targetUsername })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok) setInvitedUsernames(prev => [...prev, targetUsername]);
          else alert(data.error || "Failed to send invite.");
      } catch (e) { console.error("Invite error", e); }
  };

  const handleRespondInvite = async (action) => {
      try {
          const res = await fetch(`/api/groups/${group.slug}/invite/respond`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name, action })
          });
          if (res.ok) { fetchGroup(); fetchUserGroups(); }
      } catch (e) { console.error("Invite response error", e); }
  };

  // --- LEAVE GROUP FUNCTION ---
  const handleLeaveGroup = async () => {
      const isOnlyMember = group.members?.length === 1 && group.members[0].username === user.name;
      const confirmMsg = isOnlyMember
          ? `You're the only member of "${group.name}". Leaving will permanently disband this group, and its name will become available for anyone to reuse. Continue?`
          : "Are you sure you want to leave this group?";
      if(!window.confirm(confirmMsg)) return;
      try {
          const res = await fetch(`/api/groups/${group.slug}/leave`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name })
          });
          const data = await res.json().catch(() => ({}));
          if(res.ok) {
              if (data.disbanded) {
                  alert("Group disbanded.");
                  onBack();
              } else {
                  alert("You have left the group.");
                  fetchGroup(); // Refresh state to update UI
                  fetchUserGroups();
              }
          } else {
              alert(data.error || "Failed to leave group.");
          }
      } catch (e) { console.error("Leave error", e); }
  };

  const handleDeleteGroup = async () => {
      const confirmName = prompt(`To delete this group, type "${group.name}" below:`);
      if (confirmName !== group.name) return;
      try {
          const res = await fetch(`/api/groups/${group.id}`, { method: 'DELETE' });
          if (res.ok) { alert("Group deleted."); onBack(); } else { alert("Delete failed."); }
      } catch (e) { console.error("Delete error", e); }
  };

  const handleUpdateGroup = async (updatedData) => {
      setGroup(updatedData); 
      try {
          await fetch(`/api/groups/${group.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedData)
          });
      } catch (e) { console.error(e); }
  };

  const handleSaveSettings = () => {
      handleUpdateGroup({ ...group, ...editFields });
      setIsSettingsOpen(false);
  };

  const handleMemberRoleChange = (targetUsername, newRole) => {
      const updatedMembers = editFields.members.map(m => 
          (m.username === targetUsername) ? { ...m, role: newRole } : m
      );
      setEditFields({ ...editFields, members: updatedMembers });
  };

  const handleKickMember = (targetUsername) => {
      if(!window.confirm("Remove this member?")) return;
      const updatedMembers = editFields.members.filter(m => m.username !== targetUsername);
      setEditFields({ ...editFields, members: updatedMembers });
  };

  const handleUpload = async (e, field) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.name);
      try {
          const res = await fetch('/api/upload', { method: 'POST', body: formData });
          const data = await res.json();
          const key = field === 'banner' ? 'banner_url' : 'icon_url';
          setEditFields(prev => ({ ...prev, [key]: data.url }));
      } catch (err) { console.error("Upload error:", err); }
  };

  if (!group) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Group Details...</div>;

  const groupHeaderBg = group.banner_url ? `url(${group.banner_url}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)';

  return (
    <div className="feed-container">

      <div className="card sticky-header-bar" style={{ display: 'flex', alignItems: 'center', padding: '8px 20px', minHeight: '50px', background: groupHeaderBg }}>
        <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</h2>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        <button onClick={onBack} style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10, background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer' }}>← Back</button>

        <div style={{ height: '200px', background: group.banner_url ? `url(${group.banner_url}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)' }}>
            {canManage && (
                <button onClick={() => setIsSettingsOpen(true)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', fontSize: '18px' }}>⚙️</button>
            )}
        </div>
        
        <div style={{ padding: '0 30px 30px 30px', marginTop: '-50px', textAlign: 'center' }}>
            <div style={{ width: '100px', height: '100px', borderRadius: '15px', background: '#333', margin: '0 auto 15px', border: '4px solid #2b2b36', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {group.icon_url ? <img src={group.icon_url} alt="Group" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '40px' }}>👥</span>}
            </div>
            
            <h2 style={{ fontSize: '28px', marginBottom: '10px', cursor: 'pointer' }} onClick={() => window.location.reload()}>{group.name}</h2>
            <p style={{ maxWidth: '600px', margin: '0 auto 20px', color: '#a0a0b0', lineHeight: '1.5' }}>{group.description}</p>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 15px', borderRadius: '20px', fontSize: '12px' }}>{group.member_count} Members</span>
                {isMember ? (
                    <>
                        <span style={{ background: canManage ? '#ff4757' : 'rgba(46, 213, 115, 0.2)', color: canManage ? 'white' : '#2ed573', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', textTransform: 'uppercase', fontWeight: 'bold' }}>{userRole}</span>
                        {canInvite && <button onClick={handleOpenInvite} style={{ background: 'rgba(30, 144, 255, 0.2)', color: '#1e90ff', border: '1px solid #1e90ff', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>+ Invite</button>}
                    </>
                ) : group.invited_by ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#a0a0b0' }}>Invited by @{group.invited_by}</span>
                        <button onClick={() => handleRespondInvite('accept')} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>Accept</button>
                        <button onClick={() => handleRespondInvite('decline')} style={{ background: 'transparent', border: '1px solid #ff4757', color: '#ff4757', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>Decline</button>
                    </div>
                ) : (
                    <button onClick={handleJoinGroup} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 20px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>+ Join Group</button>
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '20px' }}>
                {['feed', 'gallery', 'about'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '15px', background: 'transparent', border: 'none', color: activeTab === tab ? '#ff4757' : '#a0a0b0', borderBottom: activeTab === tab ? '2px solid #ff4757' : 'none', fontWeight: 'bold', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}>{tab}</button>
                ))}
            </div>
        </div>
      </div>

      {activeTab === 'feed' && (
          isMember ? (
            <Feed
                user={user} posts={groupPosts}
                hasMore={groupPostsHasMore} onLoadMore={loadMoreGroupPosts} loadingMore={groupPostsLoading}
                onAddPost={handleLocalAddPost}
                groupContext={groupSlug}
                userGroups={userGroups}
                onDeletePost={handleLocalDeletePost} onLikePost={handleLocalLikePost} onAddComment={handleLocalAddComment} onVoteComment={handleLocalVoteComment}
                onDeleteComment={handleLocalDeleteComment} onEditComment={handleLocalEditComment} onEditPost={handleLocalEditPost}
                onNavigateProfile={onNavigateProfile}
                onTriggerLogin={onTriggerLogin} 
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888', background: '#1e1e24', marginTop: '10px', borderRadius: '15px' }}>
                <h3>🔒 Members Only</h3>
                {group.invited_by ? (
                    <>
                        <p>@{group.invited_by} invited you to join this community.</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                            <button onClick={() => handleRespondInvite('accept')} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '10px 25px', borderRadius: '20px', fontSize: '1rem', cursor: 'pointer', fontWeight: 'bold' }}>Accept Invite</button>
                            <button onClick={() => handleRespondInvite('decline')} style={{ background: 'transparent', border: '1px solid #ff4757', color: '#ff4757', padding: '10px 25px', borderRadius: '20px', fontSize: '1rem', cursor: 'pointer', fontWeight: 'bold' }}>Decline</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p>You must join this community to view and post content.</p>
                        <button onClick={handleJoinGroup} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '10px 25px', borderRadius: '20px', fontSize: '1rem', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' }}>Join Now</button>
                    </>
                )}
            </div>
          )
      )}

      {activeTab === 'gallery' && (
          <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ marginBottom: '20px' }}>Group Photos</h3>
              {groupGallery.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                      {groupGallery.map(p => (
                          <img key={p.id} src={p.image} alt="Gallery" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }} onClick={() => setLightboxImage(p.image)} />
                      ))}
                  </div>
              ) : <div style={{ color: '#666', textAlign: 'center' }}>No photos yet.</div>}
          </div>
      )}

      {activeTab === 'about' && (
          <div className="card" style={{ padding: '30px' }}>
              <h3 style={{ color: '#ff4757' }}>Group Rules</h3>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: '30px' }}>{group.rules || "1: No Promotions or Spam:\nSelf-promotion (includes other groups), spam, and irrelevant links aren't allowed. Keep it related to the topic of the group. Message an Admin with any questions.\n\n2: Be Kind and Courteous:\nNo hating on others. We're all in this together to create a welcoming environment. Let's treat everyone with respect.\n\n3: Zero Tolerance:\nInstant ban. Enough said. Report any malicious activity to an Admin.\n\n4: Failure to Follow Rules:\nFailure to follow these simple rules (no excuse for not reading the rules) will result in a ban."}</p>
              <h3 style={{ color: '#2ed573', marginBottom: '15px' }}>Leadership</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '30px' }}>
                  {group.members?.filter(m => m.role === 'admin' || m.role === 'moderator').map(m => (
                      <div key={m.username} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.username[0].toUpperCase()}</div>
                          <div><div style={{ fontWeight: 'bold' }}>{m.username}</div><div style={{ fontSize: '10px', color: '#a0a0b0', textTransform: 'uppercase' }}>{m.role}</div></div>
                      </div>
                  ))}
              </div>
              <h3 style={{ color: '#1e90ff', marginBottom: '15px' }}>Community</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {group.members?.filter(m => !m.role || m.role === 'member').map(m => (
                      <div key={m.username} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.username[0].toUpperCase()}</div>
                          <div><div style={{ fontWeight: 'bold' }}>{m.username}</div></div>
                      </div>
                  ))}
              </div>

              {/* Creator is no longer exempt - see v2.15 changelog above.
                  Any member (including the creator) can attempt to leave;
                  the backend enforces the actual admin-count rule and
                  handleLeaveGroup surfaces its real error message. */}
              {isMember && (
                  <div style={{ marginTop: '30px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                      <button onClick={handleLeaveGroup} style={{ background: 'transparent', border: '1px solid #ff4757', color: '#ff4757', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold' }}>Leave Group</button>
                  </div>
              )}
          </div>
      )}

      {isSettingsOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '600px', height: '80vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '20px', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>Group Settings</h3>
                      <button onClick={() => setIsSettingsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', background: 'rgba(0,0,0,0.2)' }}>
                      {['general', 'rules', 'members'].map(t => (
                          <button key={t} onClick={() => setSettingsTab(t)} style={{ flex: 1, padding: '15px', background: settingsTab === t ? 'rgba(255,255,255,0.05)' : 'transparent', border: 'none', color: settingsTab === t ? '#ff4757' : '#aaa', cursor: 'pointer', textTransform: 'uppercase', fontWeight: 'bold' }}>{t}</button>
                      ))}
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                      {settingsTab === 'general' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                              <label>Group Name <input className="post-input" value={editFields.name} onChange={(e) => setEditFields({...editFields, name: e.target.value})} /></label>
                              <label>Description <textarea className="post-input" style={{ minHeight: '80px' }} value={editFields.description} onChange={(e) => setEditFields({...editFields, description: e.target.value})} /></label>
                              <label>Upload Banner <input type="file" onChange={(e) => handleUpload(e, 'banner')} /></label>
                              <label>Upload Icon <input type="file" onChange={(e) => handleUpload(e, 'icon')} /></label>
                              <label>Who can invite people?
                                  <select value={editFields.invite_permission} onChange={(e) => setEditFields({...editFields, invite_permission: e.target.value})} style={{ display: 'block', marginTop: '5px', background: '#222', color: 'white', border: '1px solid #444', padding: '8px', borderRadius: '4px', width: '100%' }}>
                                      <option value="admins_only">Admins only</option>
                                      <option value="all_members">All members</option>
                                  </select>
                              </label>
                              <div style={{ marginTop: '30px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                                  <button onClick={handleDeleteGroup} style={{ width: '100%', background: '#ff4757', color: 'white', border: 'none', padding: '10px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>⚠️ DELETE THIS GROUP</button>
                              </div>
                          </div>
                      )}
                      {settingsTab === 'rules' && (
                          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <label style={{ marginBottom: '10px' }}>Group Rules</label>
                              <textarea className="post-input" style={{ flex: 1, minHeight: '300px', resize: 'none' }} value={editFields.rules} onChange={(e) => setEditFields({...editFields, rules: e.target.value})} />
                          </div>
                      )}
                      {settingsTab === 'members' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {editFields.members.map(member => (
                                  <div key={member.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><div style={{ width: '30px', height: '30px', background: '#555', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{member.username[0].toUpperCase()}</div><span>{member.username}</span></div>
                                      {user.name !== member.username && (<div style={{ display: 'flex', gap: '5px' }}><select value={member.role} onChange={(e) => handleMemberRoleChange(member.username, e.target.value)} style={{ background: '#222', color: 'white', border: '1px solid #444', padding: '5px', borderRadius: '4px' }}><option value="member">Member</option><option value="moderator">Moderator</option><option value="admin">Admin</option></select><button onClick={() => handleKickMember(member.username)} style={{ background: '#ff4757', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer' }}>Kick</button></div>)}
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
                  <div style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', textAlign: 'right' }}>
                      <button onClick={handleSaveSettings} style={{ padding: '10px 30px', background: '#2ed573', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer' }}>Save Changes</button>
                  </div>
              </div>
          </div>
      )}

      {showInviteModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 5000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '400px', maxHeight: '80vh', overflow: 'hidden', padding: '20px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <h3 style={{ margin: 0 }}>Invite Friends</h3>
                      <button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>
                  <div style={{ marginBottom: '15px', position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '12px', top: '10px', color: '#aaa' }}>🔍</div>
                      <input autoFocus type="text" placeholder="Search friends..." value={inviteSearch} onChange={(e) => setInviteSearch(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', outline: 'none' }} />
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1, width: '100%', marginTop: '10px', textAlign: 'left' }}>
                      {inviteCandidates.filter(u => u.name.toLowerCase().includes(inviteSearch.toLowerCase()) || (u.handle || '').toLowerCase().includes(inviteSearch.toLowerCase())).map(u => (
                          <div key={u.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', width: '100%', boxSizing: 'border-box' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  {u.profile_pic ? <img src={u.profile_pic} alt={u.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', margin: 0, display: 'block' }} /> : <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: u.avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{u.name[0]}</div>}
                                  <div><div style={{ fontWeight: 'bold' }}>{u.name}</div><div style={{ fontSize: '12px', color: '#aaa' }}>{u.handle}</div></div>
                              </div>
                              {invitedUsernames.includes(u.name) ? (
                                  <span style={{ fontSize: '12px', color: '#2ed573', fontWeight: 'bold' }}>Invited</span>
                              ) : (
                                  <button onClick={() => handleSendInvite(u.name)} style={{ background: '#1e90ff', border: 'none', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>Invite</button>
                              )}
                          </div>
                      ))}
                      {inviteCandidates.length === 0 && <div style={{ padding: '20px', textAlign: 'center', color: '#555' }}>No friends available to invite.</div>}
                  </div>
              </div>
          </div>
      )}

      {lightboxImage && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setLightboxImage(null)}>
              <img src={lightboxImage} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '10px' }} alt="Fullscreen" />
          </div>
      )}
    </div>
  );
}

export default GroupDetails;
